import {
  Button,
  TextField,
  MenuItem,
  Box,
  DialogActions,
  DialogContent,
  Stack,
  DialogTitle,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  datetimeToServer,
  prettify,
  renderDateUTC,
  renderDatetimeUTC,
} from "../../../utils/utils";
import { useAuthUser } from "react-auth-kit";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LoadingPaper } from "../../LoadingComponents";
import { useGetPatients } from "../../PatientComponents/";
import { useGetDoctors } from "../../DoctorComponents";
import { useNewTelevisit } from "../useTelevisitQueries";
import { useState } from "react";
import { useEffect } from "react";
import { useQueryClient } from "react-query";


const validationSchema = yup.object({
  date: yup
    .date()
    .nullable()
    .required("Data appuntamento obbligatoria"),

  patient_id: yup
    .number()
    .typeError("Paziente obbligatorio")
    .required("Paziente obbligatorio"),

  doctor_id: yup
    .number()
    .nullable()
    .when("$isDoctor", {
      is: false,
      then: (schema) =>
        schema.required("Professionista sanitario obbligatorio"),
    }),

  // NON required: backend li sovrascrive o li accetta vuoti
  subject: yup.string(),
  note: yup.string(),
  link: yup.string(),

  colleague1_id: yup.number().nullable(),
  colleague2_id: yup.number().nullable(),
  colleague3_id: yup.number().nullable(),

  send_patient_email: yup.boolean(),
  send_patient_sms: yup.boolean(),
  send_colleagues_email: yup.boolean(),
  send_colleagues_sms: yup.boolean(),
});

function AddTelevisit({ closeModal }) {
  // === authentication ===
  const authUser = useAuthUser();
  const queryClient = useQueryClient();

  // === web service queries/mutation ===
  const patientsQuery = useGetPatients();
  const doctorsQuery = useGetDoctors();

  // remove myself from available doctors
  useEffect(() => {
    if (
      doctorsQuery.isFetched &&
      authUser()?.role === 3 &&
      doctorsQuery.data.doctors !== undefined
    ) {
      let doctors = doctorsQuery.data.doctors.filter(
        (el) => el.doctor_id !== authUser().user.doctor_id
      );
      queryClient.setQueryData(["doctors"], (oldData) => {
        return [...doctors];
      });
    }
  }, [doctorsQuery.status]);

  const formik = useFormik({
    initialValues: {
      date: null,
      subject: "",
      link: "",
      note: "",
      report: "",
      send_patient_email: false,
      send_patient_sms: false,
      send_colleagues_email: false,
      send_colleagues_sms: false,
      patient_id: "",
      doctor_id: "",
      colleague1_id: "",
      colleague2_id: "",
      colleague3_id: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // doctor_id
      let doc =
        authUser()?.role === 3 ? authUser().user.doctor_id : values.doctor_id;

      // fix colleagues
      let colleagues = [
        values.colleague1_id,
        values.colleague2_id,
        values.colleague3_id,
      ];

      colleagues = colleagues.filter((el) => el !== "");

      // fix date and colleagues
      let newTelevisit = {
        ...values,
        doctor_id: doc,
        date: renderDatetimeUTC(values.date), //datetimeToServer(values.date),
        colleague1_id: colleagues[0] === undefined ? null : colleagues[0],
        colleague2_id: colleagues[1] === undefined ? null : colleagues[1],
        colleague3_id: colleagues[2] === undefined ? null : colleagues[2],
      };

      // if superadmin, add org_id
      if (authUser()?.role === 1) {
        newTelevisit = {
          ...newTelevisit,
          org_id: selectedDoctorOrgId,
        };
      }
      newTelevisitMutation.mutate(newTelevisit);
    },
  });

  // === web service querying - POST new Service ===
  const newTelevisitMutation = useNewTelevisit(closeModal);

  const selectedDoctorId =
    authUser()?.role === 3
      ? authUser().user.doctor_id
      : formik.values.doctor_id
      ? Number(formik.values.doctor_id)
      : null;

  const doctorsList = doctorsQuery.data?.doctors || [];
  const selectedDoctorOrgId =
    selectedDoctorId !== null
      ? doctorsList.find((doc) => doc.id === selectedDoctorId)?.org_id ?? null
      : null;

  const colleaguePool =
    selectedDoctorOrgId !== null
      ? doctorsList.filter((doc) => doc.org_id === selectedDoctorOrgId)
      : doctorsList;

  const patientsList = patientsQuery.data?.patients || [];
  const filteredPatients =
    selectedDoctorOrgId !== null
      ? patientsList.filter((pat) => pat.org_id === selectedDoctorOrgId)
      : patientsList;

  const selectedColleagueIds = [
    formik.values.colleague1_id && Number(formik.values.colleague1_id),
    formik.values.colleague2_id && Number(formik.values.colleague2_id),
    formik.values.colleague3_id && Number(formik.values.colleague3_id),
  ].filter(Boolean);

  const colleagueOptionsFor = (currentValue) =>
    colleaguePool.filter(
      (doctor) =>
        doctor.id !== selectedDoctorId &&
        (!selectedColleagueIds.includes(doctor.id) ||
          doctor.id === Number(currentValue))
    );

  const handleDoctorChange = (event) => {
    const newDoctorId = event.target.value === "" ? "" : Number(event.target.value);
    formik.setFieldValue("doctor_id", newDoctorId, true);

    // reset colleagues when doctor changes
    formik.setFieldValue("colleague1_id", "", true);
    formik.setFieldValue("colleague2_id", "", true);
    formik.setFieldValue("colleague3_id", "", true);
    // reset patient when doctor changes
    formik.setFieldValue("patient_id", "", true);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle>Nuova televisita</DialogTitle>
      <DialogContent>
        {patientsQuery.isLoading ||
        doctorsQuery.isLoading ||
        doctorsQuery.data.doctors === undefined ? (
          <LoadingPaper />
        ) : (
          <Stack direction="row" spacing={8} sx={{ width: "100%" }}>
            <Box sx={{ float: "left", width: "50%" }}>
              {/* Only superadmin/admin can choose doctor */}
              {authUser()?.role !== 3 && (
                <TextField
                  select
                  fullWidth
                  margin="dense"
                  id="doctor_id"
                  name="doctor_id"
                  label="Professionista Sanitario (*)"
                  defaultValue={""}
                  value={formik.values.doctor_id}
                  onChange={handleDoctorChange}
                  error={
                    formik.touched.doctor_id && Boolean(formik.errors.doctor_id)
                  }
                  helperText={
                    formik.touched.doctor_id && formik.errors.doctor_id
                  }
                  variant="standard"
                >
                  {doctorsQuery.data.doctors.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.first_name} {option.last_name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                fullWidth
                margin="dense"
                id="patient_id"
                name="patient_id"
                label="Paziente (*)"
                defaultValue={""}
                value={formik.values.patient_id}
                onChange={formik.handleChange}
                error={
                  formik.touched.patient_id && Boolean(formik.errors.patient_id)
                }
                helperText={
                  formik.touched.patient_id && formik.errors.patient_id
                }
                variant="standard"
                disabled={!selectedDoctorId}
              >
                {filteredPatients.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.first_name} {option.last_name}
                  </MenuItem>
                ))}
              </TextField>

              <br />
              <br />
              <br />
              <br />

              <TextField
                select
                fullWidth
                variant="standard"
                margin="dense"
                id="colleague1_id"
                name="colleague1_id"
                label="Collega invitato"
                defaultValue={""}
                value={formik.values.colleague1_id}
                onChange={formik.handleChange}
                error={
                  formik.touched.colleague1_id &&
                  Boolean(formik.errors.colleague1_id)
                }
                disabled={!selectedDoctorId}
                helperText={
                  formik.touched.colleague1_id && formik.errors.colleague1_id
                }
              >
                {colleagueOptionsFor(formik.values.colleague1_id).map(
                  (option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.first_name} {option.last_name}
                    </MenuItem>
                  )
                )}
              </TextField>
              <TextField
                select
                fullWidth
                variant="standard"
                margin="dense"
                id="colleague2_id"
                name="colleague2_id"
                label="Collega invitato"
                defaultValue={""}
                value={formik.values.colleague2_id}
                onChange={formik.handleChange}
                error={
                  formik.touched.colleague2_id &&
                  Boolean(formik.errors.colleague2_id)
                }
                disabled={
                  !selectedDoctorId ||
                  !formik.values.colleague1_id
                }
                helperText={
                  formik.touched.colleague2_id && formik.errors.colleague2_id
                }
              >
                {colleagueOptionsFor(formik.values.colleague2_id).map(
                  (option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.first_name} {option.last_name}
                    </MenuItem>
                  )
                )}
              </TextField>

              <TextField
                select
                fullWidth
                variant="standard"
                margin="dense"
                id="colleague3_id"
                name="colleague3_id"
                label="Collega invitato"
                defaultValue={""}
                value={formik.values.colleague3_id}
                disabled={
                  !selectedDoctorId ||
                  !formik.values.colleague1_id ||
                  !formik.values.colleague2_id
                }
                onChange={formik.handleChange}
                error={
                  formik.touched.colleague3_id &&
                  Boolean(formik.errors.colleague3_id)
                }
                helperText={
                  formik.touched.colleague3_id && formik.errors.colleague3_id
                }
              >
                {colleagueOptionsFor(formik.values.colleague3_id).map(
                  (option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.first_name} {option.last_name}
                    </MenuItem>
                  )
                )}
              </TextField>
            </Box>

            <Box sx={{ float: "right", width: "50%" }}>
              <DateTimePicker
                disablePast
                id="date"
                name="date"
                format="DD/MM/YYYY hh:mm A"
                label="Data appuntamento (*)"
                sx={{ marginTop: "2ch" }}
                value={formik.values.date}
                onChange={(value) => formik.setFieldValue("date", value, true)}
                slotProps={{
                  textField: {
                    variant: "standard",
                    error: formik.touched.date && Boolean(formik.errors.date),
                    helperText: formik.touched.date && formik.errors.date,
                  },
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                id="subject"
                name="subject"
                label="Oggetto"
                type="text"
                variant="standard"
                onChange={formik.handleChange}
                value={formik.values.subject}
              />

              <TextField
                margin="dense"
                fullWidth
                id="note"
                name="note"
                label="Note"
                type="text"
                variant="standard"
                onChange={formik.handleChange}
                value={formik.values.note}
              />

              <Box sx={{ marginTop: "20px" }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="send_patient_email"
                      value={formik.values.send_patient_email}
                      checked={formik.values.send_patient_email}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Invia email Paziente"
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="send_patient_sms"
                      value={formik.values.send_patient_sms}
                      checked={formik.values.send_patient_sms}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Invia sms Paziente"
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="send_colleagues_email"
                      value={formik.values.send_colleagues_email}
                      checked={formik.values.send_colleagues_email}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Invia email Colleghi"
                  labelPlacement="start"
                />
                <FormControlLabel
                  control={
                    <Switch
                      name="send_colleagues_sms"
                      value={formik.values.send_colleagues_sms}
                      checked={formik.values.send_colleagues_sms}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Invia sms Colleghi"
                  labelPlacement="start"
                />
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={newTelevisitMutation.isLoading} type="submit">
          Conferma
        </Button>
        <Button
          disabled={newTelevisitMutation.isLoading}
          onClick={formik.handleReset}
        >
          Reset
        </Button>
        <Button
          disabled={newTelevisitMutation.isLoading}
          color="error"
          onClick={closeModal}
        >
          Annulla
        </Button>
      </DialogActions>
    </form>
  );
}

export default AddTelevisit;
